import { useController, type Control } from "react-hook-form";

import { MAX_ASSIGNEES, useAssigneeOptions } from "@/features/tasks/hooks/useAssigneeOptions";
import type { NewTaskInput, NewTaskOutput } from "@/features/tasks/lib/newTaskForm";
import { useT } from "@/shared/i18n/useT";
import { DateField } from "@/shared/ui/DateField";
import { Field, describedBy } from "@/shared/ui/Field";
import { MultiCombobox } from "@/shared/ui/MultiCombobox";

interface NewTaskPeopleRowProps {
  control: Control<NewTaskInput, unknown, NewTaskOutput>;
  /** The task's project, whose members it can go to; null offers all active staff. */
  projectKey: string | null;
  enabled: boolean;
  assigneeError: string | undefined;
  dueError: string | undefined;
}

/** Who does the new task and when it is due, side by side from small screens up. */
export function NewTaskPeopleRow({
  control,
  projectKey,
  enabled,
  assigneeError,
  dueError,
}: NewTaskPeopleRowProps) {
  const t = useT();
  const assignees = useAssigneeOptions(projectKey, { enabled });
  const assignee = useController({ control, name: "assignee_ids" });
  const dueOn = useController({ control, name: "due_on" });

  return (
    <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_11rem]">
      <Field id="task-assignee" label={t("task.assignees")} error={assigneeError}>
        <MultiCombobox
          size="md"
          options={assignees.people}
          value={assignee.field.value}
          onChange={assignee.field.onChange}
          max={MAX_ASSIGNEES}
          fullLabel={t("task.assigneesFull", { count: MAX_ASSIGNEES })}
          placeholder={t("task.unassigned")}
          searchPlaceholder={t("task.assigneeSearch")}
          emptyLabel={t("work.noPeopleMatch")}
          disabled={assignees.isPending}
          {...describedBy("task-assignee", assigneeError)}
        />
      </Field>
      <Field id="task-due" label={t("task.dueOn")} error={dueError}>
        <DateField
          size="md"
          value={dueOn.field.value}
          onChange={dueOn.field.onChange}
          onBlur={dueOn.field.onBlur}
          {...describedBy("task-due", dueError)}
        />
      </Field>
    </div>
  );
}
